import { Users } from "lucide-react";
import type { Timekeeper } from "@/features/attorney/views/Entries/timekeepers";
import { formatTimekeeperFullName } from "@/features/attorney/views/Entries/timekeepers";
import { Button } from "@/v2/components/ui/button";
import { Checkbox } from "@/v2/components/ui/checkbox";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";

interface TimekeeperFilterPopoverProps {
	timekeepers: readonly Timekeeper[] | null;
	selectedIds: readonly string[];
	defaultIds: readonly string[];
	onChange: (ids: readonly string[]) => void;
}

export function TimekeeperFilterPopover({
	timekeepers,
	selectedIds,
	defaultIds,
	onChange,
}: TimekeeperFilterPopoverProps) {
	if (!timekeepers || timekeepers.length < 2) return null;

	const selected = new Set(selectedIds);
	const active =
		selectedIds.length !== defaultIds.length ||
		selectedIds.some((id) => !defaultIds.includes(id));
	const allIds = timekeepers.map((timekeeper) => timekeeper.id);
	const toggle = (id: string) => {
		onChange(
			selected.has(id)
				? selectedIds.filter((selectedId) => selectedId !== id)
				: [...selectedIds, id],
		);
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					aria-label="Filter by timekeeper"
					className="relative h-10 w-10 text-muted-foreground"
				>
					<Users className="size-5" />
					{active && (
						<span className="absolute right-1 top-1 size-1.5 rounded-full bg-ring" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-72 p-0" align="start">
				<div className="flex items-center justify-between px-3 pb-1.5 pt-3">
					<span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
						Timekeepers
					</span>
					<div className="flex items-center gap-1.5">
						<button
							type="button"
							onClick={() => onChange(allIds)}
							className="text-xs text-primary hover:underline"
						>
							All
						</button>
						<span className="text-xs text-muted-foreground">/</span>
						<button
							type="button"
							onClick={() => onChange([])}
							className="text-xs text-primary hover:underline"
						>
							None
						</button>
					</div>
				</div>
				<div className="max-h-72 overflow-y-auto px-1.5 pb-1.5">
					{timekeepers.map((timekeeper) => (
						<button
							type="button"
							key={timekeeper.id}
							onClick={() => toggle(timekeeper.id)}
							className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left hover:bg-accent"
						>
							<Checkbox
								checked={selected.has(timekeeper.id)}
								tabIndex={-1}
								aria-hidden
								className="pointer-events-none"
							/>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm">
									{formatTimekeeperFullName(timekeeper)}
								</div>
								{timekeeper.email && (
									<div className="truncate text-xs text-muted-foreground">
										{timekeeper.email}
									</div>
								)}
							</div>
						</button>
					))}
				</div>
				{active && (
					<div className="border-t border-border p-1.5">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onChange(defaultIds)}
							className="h-7 w-full text-xs text-muted-foreground hover:text-foreground"
						>
							Reset to me
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
